/**
 * MOMENTUM_TRACKER — Power Trend Condition Buildup Tracker
 *
 * Sub-pane (80 px) indicator showing how many of the 4 power-trend conditions
 * are satisfied on each bar. A visual history of the setup building up before
 * the signal fires.
 *
 * Bar colours:
 *   0 conditions met  → hairline (dim)
 *   1 condition met   → amber
 *   2 conditions met  → orange
 *   3 conditions met  → green (C1+C2+C3 satisfied, waiting for C4/green-close)
 *   In power trend    → full-height solid bright green
 *
 * A dashed threshold line at 75% pane height marks the "signal-ready" level.
 */

import { IndicatorSeries, LineType, type IndicatorTemplate } from 'klinecharts';
import { computeMomentum } from './momentumOverlay';

// ── Shared types ──────────────────────────────────────────────────────────────

type KBar = { open: number; high: number; low: number; close: number };

type TrackerResult = {
  condCount: number | null; // 0–3 = conditions met (not in trend); null if insufficient data
  in_trend:  boolean;
};

// ── Draw params ───────────────────────────────────────────────────────────────

type DrawParams = {
  ctx:          CanvasRenderingContext2D;
  visibleRange: { from: number; to: number };
  bounding:     { left: number; top: number; right: number; bottom: number; width: number; height: number };
  xAxis:        { convertToPixel: (i: number) => number };
  indicator:    { result: TrackerResult[] };
  barSpace:     { bar: number; gapBar: number; halfGapBar: number };
};

// ── Colour palette ────────────────────────────────────────────────────────────

const COLOR_0       = 'rgba(255,255,255,0.06)'; // hairline
const COLOR_1       = 'rgba(255,200,0,0.55)';   // amber — 1 condition
const COLOR_2       = 'rgba(255,130,0,0.70)';   // orange — 2 conditions
const COLOR_3       = 'rgba(34,197,94,0.55)';   // green — 3 conditions (ready)
const COLOR_TREND   = 'rgba(34,197,94,0.88)';   // bright green — in trend
const THRESHOLD_CLR = 'rgba(34,197,94,0.35)';   // dashed threshold line

const LEGEND_H = 20; // px reserved for klinecharts pane legend text

// ── Indicator template ────────────────────────────────────────────────────────

const momentumTracker: IndicatorTemplate<TrackerResult> = {
  name:       'MOMENTUM_TRACKER',
  shortName:  'MomTracker',
  series:     IndicatorSeries.Normal,
  shouldOhlc: false,

  // One hidden line drives klinecharts y-axis auto-scale (0–3 range)
  figures: [
    { key: 'condCount', title: 'Conditions: ', type: 'line' },
  ],
  styles: {
    lines: [
      { style: LineType.Solid, size: 0, color: 'transparent', smooth: false, dashedValue: [2, 2] },
    ],
  },

  // ── calc ─────────────────────────────────────────────────────────────────
  // Re-computes the full momentum state independently from momentumOverlay so
  // klinecharts can call calc() on each indicator in any order.
  calc: (dataList) => {
    const bars    = dataList as unknown as KBar[];
    const results = computeMomentum(bars);

    return results.map((r) => ({
      condCount: r.sma50 === null ? null : (r.in_trend ? 3 : r.cond_count),
      in_trend:  r.in_trend,
    }));
  },

  // ── draw ──────────────────────────────────────────────────────────────────
  draw: (params: unknown): boolean => {
    const { ctx, visibleRange, bounding, xAxis, indicator, barSpace } =
      params as DrawParams;

    const results = indicator.result;
    const { from, to } = visibleRange;
    if (to < from || !results.length) return true;

    const { gapBar, halfGapBar } = barSpace;
    const paneH  = bounding.height - LEGEND_H;
    const bottom = bounding.height; // bottom of pane (pane-local y)

    if (paneH <= 0) return true;

    ctx.save();

    // ── Draw stacked bars ─────────────────────────────────────────────────
    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r) continue;

      const x = xAxis.convertToPixel(i);

      if (r.in_trend) {
        // Full-height bright green bar
        ctx.fillStyle = COLOR_TREND;
        ctx.fillRect(x - halfGapBar, LEGEND_H, gapBar + 0.5, paneH);
      } else if (r.condCount === null || r.condCount === 0) {
        // Hairline at the bottom
        ctx.fillStyle = COLOR_0;
        ctx.fillRect(x - halfGapBar, bottom - 2, gapBar + 0.5, 2);
      } else {
        // Proportional fill: condCount/3 of pane height
        const fillH = (r.condCount / 3) * paneH;
        const color = r.condCount === 3 ? COLOR_3
                    : r.condCount === 2 ? COLOR_2
                    : COLOR_1;
        ctx.fillStyle = color;
        ctx.fillRect(x - halfGapBar, bottom - fillH, gapBar + 0.5, fillH);
      }
    }

    // ── Threshold line marking the top of the 3-condition fill ─────────────
    // condCount=3 bars fill the full paneH, so the line sits at LEGEND_H + 1
    const lineY = LEGEND_H + 1;

    ctx.strokeStyle = THRESHOLD_CLR;
    ctx.lineWidth   = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(xAxis.convertToPixel(from), lineY);
    ctx.lineTo(xAxis.convertToPixel(to),   lineY);
    ctx.stroke();
    ctx.setLineDash([]);

    // ── "READY" label at left edge ────────────────────────────────────────
    ctx.fillStyle    = THRESHOLD_CLR;
    ctx.font         = '9px sans-serif';
    ctx.textAlign    = 'left';
    ctx.textBaseline = 'bottom';
    ctx.fillText('READY', xAxis.convertToPixel(from) + 2, lineY - 1);

    ctx.restore();
    return true;
  },

  // ── tooltip ───────────────────────────────────────────────────────────────
  createTooltipDataSource: (params: unknown) => {
    const p = params as { indicator: { result: TrackerResult[] }; crosshair: { dataIndex?: number } };
    const idx = p.crosshair.dataIndex ?? -1;
    const r = p.indicator.result[idx];
    if (!r) return { name: 'Momentum Tracker', calcParamsText: '', icons: [], values: [] };

    const label = r.in_trend ? 'In Trend' : r.condCount === null ? 'Warming up' : `${r.condCount}/3 conditions`;
    return {
      name: 'Momentum Tracker',
      calcParamsText: '',
      icons: [],
      values: [{ title: 'Status: ', value: label }],
    };
  },
};

export default momentumTracker;
